/** "Ask Tala to refine" — applicable suggestions (founder walk, round 2).
 *
 *  The agent backend instructs Tala (surfaces `domains` / `onboarding`) to END a refine reply
 *  with a compact machine block, one suggestion per line:
 *
 *      ADD-ENTITY: name — one-line why
 *      ADD-EDGE: name — why
 *      ADD-EGO: name (event|state) — why
 *
 *  This module is the tolerant parser for that block plus the pure merge helpers the apply
 *  chips use. Everything here is pure (no DOM, no I/O) and unit-tested directly. A reply with
 *  no machine block degrades to prose-only — the chips simply don't render.
 */

import type { EgoRelationKind, EgoRelationSpec } from '@/api/domains'

export type SuggestionKind = 'entity' | 'edge' | 'ego'

export interface TalaSuggestion {
  kind: SuggestionKind
  /** Normalized lowercase_snake vocabulary word — matches the builder's field norms. */
  name: string
  /** Only meaningful when kind === 'ego'; defaults to 'event' when Tala omits it. */
  egoKind: EgoRelationKind
  /** Tala's one-line reasoning — surfaced as the chip's tooltip; may be empty. */
  why: string
}

export interface ParsedTalaReply {
  /** The reply with the machine block removed — rendered as Markdown. */
  prose: string
  suggestions: TalaSuggestion[]
}

/** The event/state glosses — the ONE copy every kind-picker HelpHint uses. */
export const EVENT_GLOSS = 'happened at a moment — builds a history (watched, met, cooked)'
export const STATE_GLOSS = 'an ongoing relationship until you change it (reading, tracking, favorite)'

export function egoKindGloss(kind: EgoRelationKind): string {
  return kind === 'state' ? STATE_GLOSS : EVENT_GLOSS
}

/** Vocabulary words are lowercase_snake — "Board Member" → board_member. Strips quote/backtick
 *  dressing first so Tala's `ticker` or "deal" normalize clean. */
export function toSnakeWord(word: string): string {
  return word
    .replace(/[`"'“”]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

// One machine line: optional bullet, optional bold marks, ADD-ENTITY / ADD_EDGE / add-ego …,
// then the body. The tag separator tolerates ':', '-', '–', '—'.
const MACHINE_LINE = /^\s*(?:[-*•]\s*)?\*{0,2}ADD[-_ ](ENTITY|EDGE|EGO)\*{0,2}\s*[:\-–—]\s*(.+?)\s*$/i

// "name — why" separator: an em/en dash splits even unspaced (they never occur inside a
// vocabulary word); '--', '-', ':' split only when spaced (kebab names carry bare hyphens).
const WHY_SEP = /\s*(?:—|–)\s*|\s+(?:--|-|:)\s+/

// "(event)" / "(state)" marker anywhere in an ADD-EGO body.
const EGO_KIND = /\(\s*(event|state)\s*\)/i

function splitWhy(body: string): { name: string; why: string } {
  const m = WHY_SEP.exec(body)
  if (!m) return { name: body, why: '' }
  return { name: body.slice(0, m.index), why: body.slice(m.index + m[0].length).trim() }
}

/** Parse a Tala reply into prose + applicable suggestions. Tolerant by design: junk lines fall
 *  through into the prose, malformed names are skipped, kind+name pairs are deduped. */
export function parseTalaReply(text: string): ParsedTalaReply {
  const proseLines: string[] = []
  const suggestions: TalaSuggestion[] = []
  const seen = new Set<string>()

  for (const line of text.split('\n')) {
    const m = MACHINE_LINE.exec(line)
    if (!m) {
      proseLines.push(line)
      continue
    }
    const kind = m[1].toLowerCase() as SuggestionKind
    let body = m[2]

    let egoKind: EgoRelationKind = 'event'
    if (kind === 'ego') {
      const km = EGO_KIND.exec(body)
      if (km) {
        egoKind = km[1].toLowerCase() as EgoRelationKind
        body = body.replace(EGO_KIND, ' ').replace(/\s{2,}/g, ' ').trim()
      }
    }

    const { name: rawName, why } = splitWhy(body)
    const name = toSnakeWord(rawName)
    if (!name) continue
    const key = `${kind}:${name}`
    if (seen.has(key)) continue
    seen.add(key)
    suggestions.push({ kind, name, egoKind, why })
  }

  const prose = proseLines.join('\n').replace(/\n{3,}/g, '\n\n').trim()
  return { prose, suggestions }
}

/** Append normalized additions to a vocabulary list, deduping by normalized form. Returns the
 *  input array untouched (same reference) when nothing new lands — cheap no-op for setState. */
export function mergeTypes(existing: string[], additions: string[]): string[] {
  const have = new Set(existing.map(toSnakeWord))
  let out = existing
  for (const a of additions) {
    const word = toSnakeWord(a)
    if (!word || have.has(word)) continue
    have.add(word)
    out = out === existing ? [...existing] : out
    out.push(word)
  }
  return out
}

/** Merge ego suggestions into an ego_relations map — new names land with their kind, existing
 *  specs (weight/group extras included) are never overwritten. Pure: never mutates the input. */
export function mergeEgoMap(
  existing: Record<string, EgoRelationSpec>,
  additions: { name: string; egoKind: EgoRelationKind }[],
): Record<string, EgoRelationSpec> {
  const out: Record<string, EgoRelationSpec> = { ...existing }
  for (const a of additions) {
    const name = toSnakeWord(a.name)
    if (!name || name in out) continue
    out[name] = { kind: a.egoKind }
  }
  return out
}
